import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, InputGroup, Alert, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../utils/apiUtils';
import { useNotification } from '../hooks/useNotification';
import { getStatusVariant, getTypeVariant } from '../utils/statusUtils';
import { formatDate } from '../utils/uiUtils';
import { canDeleteItem, canEditItem } from '../utils/permissionUtils';
import MonitoringToolModal from './MonitoringToolModal';

// Helper function to format proponents - removes task and keeps only honorific and name
const formatProponents = (proponentsString) => {
  if (!proponentsString) return '';
  
  // If the string contains " - ", split it and take only the part before " - "
  // This removes the task information while preserving honorific and name
  if (typeof proponentsString === 'string' && proponentsString.includes(' - ')) {
    return proponentsString.split(' - ')[0].trim();
  }
  
  return proponentsString.trim();
};

const Monitoring = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { notification, showSuccess, showError } = useNotification();
  const [monitoringData, setMonitoringData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMonitoringId, setEditingMonitoringId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [monitoringToDelete, setMonitoringToDelete] = useState(null);


  useEffect(() => {
    fetchMonitoringData();
  }, []);

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching monitoring data from /monitoring');
      const response = await apiClient.get('/monitoring');
      setMonitoringData(response.data);
      setFilteredData(response.data);
    } catch (err) {
      console.error('Error fetching monitoring data:', err);
      setError('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  // Filter monitoring data based on search term, status, and type
  useEffect(() => {
    let filtered = monitoringData;

    if (searchTerm.trim()) {
      filtered = filtered.filter(item =>
        item.extension_program_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.project_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.proponents.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.creator_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'All') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    setFilteredData(filtered);
  }, [monitoringData, searchTerm, statusFilter]);

  // Status and type variant functions moved to utils/statusUtils.js

  const getUniqueStatuses = () => {
    const statuses = [...new Set(monitoringData.map(item => item.status))];
    return ['All', ...statuses];
  };


  const clearSearch = () => {
    setSearchTerm('');
    setStatusFilter('All');
  };

  const hasActiveSearch = searchTerm.trim() || statusFilter !== 'All';

  const canManage = (item) => {
    if (!user) return false;
    if (['Extension Head', 'GAD', 'Vice Chancellor for Administration and Finance', 'Vice Chancellor for Research, Development and Extension Services', 'Chancellor', 'Admin'].includes(user.role)) return true;
    return false;
  };

  const handleView = (item) => {
    // Navigate to monitoring detail view
    navigate(`/monitoring/${item.monitoring_id}`);
  };

  const handleEdit = (item) => {
    setEditingMonitoringId(item.monitoring_id);
    setShowCreateModal(true);
  };

  const handleDelete = (item) => {
    setMonitoringToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!monitoringToDelete) return;
    
    try {
      await apiClient.delete(`/monitoring/${monitoringToDelete.monitoring_id}`);
      showSuccess('Monitoring tool deleted successfully');
      fetchMonitoringData();
      setShowDeleteModal(false);
      setMonitoringToDelete(null);
    } catch (err) {
      console.error('Error deleting monitoring tool:', err);
      showError('Failed to delete monitoring tool');
    }
  };


  if (loading) {
    return (
      <div className="monitoring-container">
        <div className="py-4 px-4">
          <div>
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading monitoring data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="monitoring-container">
      <div className="py-4 px-4">
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h3 className="mb-1">Monitoring Tools</h3>
              </div>
              <Button variant="success" onClick={() => setShowCreateModal(true)}>
                <i className="bi bi-plus-lg me-2"></i>
                Create Monitoring Tool
              </Button>
            </div>

            {/* Search and Filter Section */}
            <Card className="shadow-sm mb-3">
              <Card.Body>
                <div className="row g-3">
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Search Monitoring</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search by title, project, or coordinator..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>
                  </div>
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label>Filter by Status</Form.Label>
                      <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        {getUniqueStatuses().map(status => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    {hasActiveSearch && (
                      <Button variant="outline-secondary" onClick={clearSearch}>
                        <i className="bi bi-x-lg me-1"></i>
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Results Summary */}
            {hasActiveSearch && (
              <div className="mb-3">
                <small className="text-muted">
                  Showing {filteredData.length} of {monitoringData.length} monitoring items
                </small>
              </div>
            )}

            {/* Monitoring Data Table */}
            <Card className="shadow-sm">
              <Card.Body className="p-0">
                {error && (
                  <Alert variant="danger" className="m-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                {filteredData.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-graph-up display-1 text-muted"></i>
                    <h5 className="mt-3">No monitoring data found</h5>
                    <p className="text-muted">
                      {hasActiveSearch 
                        ? 'Try adjusting your search criteria'
                        : 'No monitoring items have been created yet'
                      }
                    </p>
                  </div>
                ) : (
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Project Title</th>
                        <th>Proponents</th>
                        <th>Status</th>
                        <th>Created By</th>
                        <th>Date</th>
                        <th>Created At</th>
                        <th style={{ width: 120 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item) => (
                        <tr key={item.monitoring_id}>
                          <td>
                            <div>
                              {item.project_title}
                            </div>
                          </td>
                          <td>{formatProponents(item.proponents)}</td>
                          <td>
                            <Badge bg={getStatusVariant(item.status)}>
                              {item.status}
                            </Badge>
                          </td>
                          <td>{item.creator_name}</td>
                          <td>{formatDate(item.date)}</td>
                          <td>{formatDate(item.created_at)}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-view-${item.monitoring_id}`}>View</Tooltip>}>
                                <Button 
                                  onClick={() => handleView(item)} 
                                  variant="link" 
                                  className="icon-only-btn icon-view" 
                                  aria-label="View monitoring tool"
                                >
                                  <i className="bi bi-eye"></i>
                                </Button>
                              </OverlayTrigger>

                              {canEditItem(user, item, 'monitoring').canEdit && (
                                <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-edit-${item.monitoring_id}`}>Edit</Tooltip>}>
                                  <Button 
                                    onClick={() => handleEdit(item)} 
                                    variant="link" 
                                    className="icon-only-btn icon-edit" 
                                    aria-label="Edit monitoring tool"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </Button>
                                </OverlayTrigger>
                              )}

                              {/* Delete Button - Admin only */}
                              {user && canDeleteItem(user, item).canDelete && (
                                <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-delete-${item.monitoring_id}`}>Delete</Tooltip>}>
                                  <Button 
                                    onClick={() => handleDelete(item)} 
                                    variant="link" 
                                    className="icon-only-btn icon-delete" 
                                    aria-label="Delete monitoring tool"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                </OverlayTrigger>
                              )}

                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
        </div>
      </div>

      {/* Create/Edit Monitoring Tool Modal */}
      <MonitoringToolModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setEditingMonitoringId(null);
        }}
        monitoringId={editingMonitoringId}
        onSuccess={() => {
          fetchMonitoringData();
          setShowCreateModal(false);
          setEditingMonitoringId(null);
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => {
        setShowDeleteModal(false);
        setMonitoringToDelete(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete "{monitoringToDelete?.extension_program_title || monitoringToDelete?.project_title}"?</p>
          <p className="text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDeleteModal(false);
            setMonitoringToDelete(null);
          }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Monitoring;
